import { Assignment } from '@mui/icons-material';
import Inventory2Icon from '@mui/icons-material/Inventory2';
import RouteIcon from '@mui/icons-material/Route';
import { Box, Paper, Radio, TextField } from '@mui/material';
import Grid from '@mui/material/Grid';
import Typography from '@mui/material/Typography';
import { GoodsInfo, MaritimeOrderInfo, TransportRouteType } from 'adoms-common-lib';
import React, { useEffect, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { CustomerURLInformationUtil } from '../../common/customerURLInformationUtil';
import { imageResizeAndConvertBase64 } from '../../common/ImageEditUtil';
import { OrderBackNextButtons } from '../../components/atoms/OrderBackNextButtons';
import ApplicationTooBar from '../../components/molecule/ApplicationToolBar';
import OrderStepBar, { Process } from '../../components/molecule/OrderStepBar';
import { MaritimeDeliveryItemInput } from '../../components/organisms/MaritimeDeliveryItemInput';
import { OrderInfoNotFound } from '../../components/organisms/OrderInfoNotFound';
import { SectionGrid } from '../../components/organisms/SectionGrid';

//　TODO: 共通化
const genId = () => {
    // Prefer crypto.randomUUID when available
    // Fallback to a simple unique string
    // @ts-ignore
    if (typeof crypto !== "undefined" && crypto.randomUUID) return crypto.randomUUID();
    return `id_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
};

export const MaritimeOrderGoodsAndDestinationInput = () => {
    const navigate = useNavigate();
    const location = useLocation();

    const state = (location.state as {
        order?: MaritimeOrderInfo;
        orderForEdit?: MaritimeOrderInfo;
    } | null | undefined);

    // URLIDコピーのためシャローコピー（null/undefined を spread すると落ちるのでガード）
    const [customerURLInformation] = React.useState(() => {
        const info = CustomerURLInformationUtil.instance.getCustomerURLInformation();
        return info ? { ...info } : undefined;
    });

    const order = state?.order;
    const orderForEdit = state?.orderForEdit;
    const resolvedOrder = (orderForEdit ?? order) as MaritimeOrderInfo | undefined;
    // 初期配送物リスト（既存編集データ > 受け取った注文 > デフォルト）
    const [goodsItems, setGoodsItems] = useState<GoodsInfo[]>(
        resolvedOrder?.goods?.length
            ? resolvedOrder.goods
            : [{ id: genId(), weight: 0, name: '', count: 1 }]
    );
    const [transportRoute, setTransportRoute] = useState<TransportRouteType>(
        resolvedOrder?.transportRoute ?? TransportRouteType.ViaCivilianAirport
    );
    const [supplyHqVoucherNumber, setSupplyHqVoucherNumber] = useState(
        resolvedOrder?.supplyHqVoucherNumber ?? ""
    );
    const [carrierWaybillNumber, setcarrierWaybillNumber] = useState(
        resolvedOrder?.carrierWaybillNumber ?? ""
    );
    const [isOverCapacity, setIsOverCapacity] = useState(false);

    useEffect(() => {
        if (transportRoute === TransportRouteType.InternalTransport && carrierWaybillNumber !== "") {
            setcarrierWaybillNumber("");
        }
    }, [transportRoute, carrierWaybillNumber]);


    useEffect(() => {
        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
        }
    }, [customerURLInformation, navigate]);

    const handleNavigateTopPage = async () => {
        const urlId = customerURLInformation?.urlId;
        if (!urlId) {
            navigate('/forbidden403Page');
            return;
        }
        await CustomerURLInformationUtil.instance.updateCustomerURLInformationByUrlId(urlId);
        navigate(`/${urlId}`);
    };

    const isEmptyGoodsItem = (g: GoodsInfo): boolean => {
        const hasName = (g.name ?? "").trim().length > 0;
        const hasWeight = (g.weight ?? 0) > 0;
        const dims = g.dimensions;
        const hasAnyDimension = !!dims && [dims.length, dims.width, dims.height].some((v) => (v ?? 0) > 0);
        return !hasName && !hasWeight && !hasAnyDimension;
    };

    const buildUpdatedOrder = (baseOrder: MaritimeOrderInfo): MaritimeOrderInfo => {
        const filteredGoodsItems = goodsItems.filter((g) => !isEmptyGoodsItem(g));
        const goodsToSave: GoodsInfo[] = filteredGoodsItems.length
            ? filteredGoodsItems.map((g) => {
                const trimmedName = (g.name ?? "").trim();
                return {
                    ...g,
                    name: trimmedName === "" ? "荷物" : trimmedName,
                };
            })
            : [{ id: genId(), weight: 0, name: '荷物一式', count: 1 } as GoodsInfo];
        return {
            ...baseOrder,
            goods: goodsToSave,
            transportRoute,
            supplyHqVoucherNumber,
            carrierWaybillNumber: transportRoute === TransportRouteType.InternalTransport ? "" : carrierWaybillNumber,
        };
    };

    const isNextDisabled =
        supplyHqVoucherNumber.trim() === "" ||
        (transportRoute !== TransportRouteType.InternalTransport && carrierWaybillNumber.trim() === "") ||
        goodsItems.length === 0 ||
        isOverCapacity;

    /**
     * 次のページに遷移する際の処理
     * @param event 
     * @param navigate 
     */
    const handleToNextPage = async (event: any, baseOrder: MaritimeOrderInfo) => {
        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
            return;
        }
        const updatedOrder = buildUpdatedOrder(baseOrder);

        navigate(
            "/ordercontactinput/" + customerURLInformation?.urlId,
            {
                state: {
                    order: updatedOrder
                },
            })
    }

    /**
     * 前のページに遷移する際の処理
     * @param event 
     * @param navigate 
     */
    const handleToBackPage = async (event: any, baseOrder: MaritimeOrderInfo) => {
        if (!customerURLInformation?.urlId) {
            navigate('/forbidden403Page');
            return;
        }
        const updatedOrder = buildUpdatedOrder(baseOrder);

        navigate(
            "/orderflightsearchinput/" + customerURLInformation?.urlId,
            {
                state: {
                    orderForEdit: updatedOrder,
                },
            });
    };

    // 配送ルート選択カードのスタイル生成関数
    const getRouteCardStyle = (value: TransportRouteType) => ({
        p: 2,
        border: '2px solid',
        borderColor: transportRoute === value ? 'primary.main' : 'transparent',
        backgroundColor: transportRoute === value ? '#e3f2fd' : '#fff',
        borderRadius: 2,
        cursor: 'pointer',
        transition: 'all 0.2s',
        display: 'block', // 確実にブロック要素として扱う
        '&:hover': {
            backgroundColor: transportRoute === value ? '#e3f2fd' : '#fafafa',
        },
    });

    if (!resolvedOrder) {
        return (
            <OrderInfoNotFound
                appBarLabel="配送依頼"
                process={Process.DestinationInput}
                message={`注文情報が取得できませんでした。\nブラウザ更新等により画面遷移時の情報が失われた可能性があります。`}
                onNavigateHome={handleNavigateTopPage}
            />
        );
    }

    return (
        <Grid container justifyContent="center" xs={12} spacing={1}>
            <Grid item xs={12} sm={12} >
                <ApplicationTooBar label="配送依頼" />
            </Grid>

            <Grid item xs={12} sm={12} >
                <OrderStepBar process={Process.DestinationInput} />
            </Grid>
            {/* 1. 配送ルート選択 */}
            <SectionGrid title="配送ルート" icon={<RouteIcon />}>
                {/* RadioGroupを削除し、Gridで直接レイアウトを制御 */}
                <Grid container spacing={2}>
                    <Grid item xs={12} sm="auto">
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={getRouteCardStyle(TransportRouteType.ViaCivilianAirport)}
                            component="label" // クリック可能にする
                        >
                            <Box display="flex" alignItems="center">
                                <Radio
                                    checked={transportRoute === TransportRouteType.ViaCivilianAirport}
                                    onChange={() => setTransportRoute(TransportRouteType.ViaCivilianAirport)}
                                    value="civilian_airport"
                                    name="route"
                                    color="primary"
                                />
                                <Box ml={1}>
                                    <Typography variant="subtitle1" fontWeight="bold">民間空港経由</Typography>
                                    <Typography variant="caption" color="textSecondary">通常の配送ルート</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                    <Grid item xs={12} sm="auto">
                        <Paper
                            elevation={0}
                            variant="outlined"
                            sx={getRouteCardStyle(TransportRouteType.InternalTransport)}
                            component="label"
                        >
                            <Box display="flex" alignItems="center">
                                <Radio
                                    checked={transportRoute === TransportRouteType.InternalTransport}
                                    onChange={() => setTransportRoute(TransportRouteType.InternalTransport)}
                                    value="internal_transport"
                                    name="route"
                                    color="primary"
                                />
                                <Box ml={1}>
                                    <Typography variant="subtitle1" fontWeight="bold">部内輸送</Typography>
                                    <Typography variant="caption" color="textSecondary">自衛隊車両等による輸送</Typography>
                                </Box>
                            </Box>
                        </Paper>
                    </Grid>
                </Grid>
            </SectionGrid>
            {/* 2. 管理情報 */}
            <SectionGrid title="管理情報" icon={<Assignment />}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            required
                            label="補給本部伝票番号"
                            name="supplyHqVoucherNumber"
                            value={supplyHqVoucherNumber}
                            onChange={(e) => setSupplyHqVoucherNumber(e.target.value)}
                            variant="outlined"
                        />
                    </Grid>
                    {transportRoute !== TransportRouteType.InternalTransport && (
                        <Grid item xs={12}>
                            <TextField
                                fullWidth
                                label="日通運送状番号"
                                name="carrierWaybillNumber"
                                value={carrierWaybillNumber}
                                onChange={(e) => setcarrierWaybillNumber(e.target.value)}
                                variant="outlined"
                            />
                        </Grid>
                    )}
                </Grid>
            </SectionGrid>
            <SectionGrid title="配送物の情報" icon={<Inventory2Icon />}>
                <MaritimeDeliveryItemInput
                    items={goodsItems}
                    setGoodsItems={setGoodsItems}
                    setIsOverCapacity={setIsOverCapacity}
                />
            </SectionGrid>

            <OrderBackNextButtons
                onBack={(e) => handleToBackPage(e, resolvedOrder)}
                onNext={(e) => handleToNextPage(e, resolvedOrder)}
                nextDisabled={isNextDisabled}
            />
        </Grid>
    );
}